import { z } from 'zod';
import { GitHostKind, GitWebhookMode } from './git.js';
/**
 * Webhook "kéo ngay" của tab Git — APM-GIT-g, docs/plan-git-integration.md §5.2.
 *
 * Webhook là **tín hiệu**, không phải dữ liệu: máy chủ git gọi vào
 * `POST <APP_URL>/api/v1/git/webhooks/<id kho dạng hex>`, ChillPM kiểm bí mật rồi
 * đánh dấu "kéo ngay" — payload không bao giờ được đọc. Tệp này chỉ mang hình
 * của **màn cấu hình** (`…/git/repositories/:repositoryId/webhook`, `git:manage`)
 * và các hằng mà cả adapter (đăng ký tự động) lẫn màn hướng dẫn dán tay cùng dùng.
 */
/** Gốc đường dẫn nhận webhook; phía sau là id kho ở dạng 32 ký tự hex. */
export const GIT_WEBHOOK_PATH = '/api/v1/git/webhooks';
/** Trần body thô của một lượt giao (§5.2). Vượt ⇒ không tính được HMAC ⇒ 200 và bỏ qua. */
export const MAX_GIT_WEBHOOK_BYTES = 1024 * 1024;
/**
 * Sự kiện phải bật trên webhook của **GitLab** — đúng tên trường của
 * `POST /projects/:id/hooks` (§9.3). Màn dán tay đổi mỗi khoá thành nhãn ô tích
 * tương ứng trên giao diện GitLab.
 */
export const GITLAB_WEBHOOK_EVENTS = [
    'push_events',
    'tag_push_events',
    'merge_requests_events',
    'note_events',
    'pipeline_events',
    'deployment_events',
    'releases_events',
];
/** Sự kiện phải chọn trên webhook của **GitHub** — đúng giá trị của `events` (§10.3). */
export const GITHUB_WEBHOOK_EVENTS = [
    'push',
    'create',
    'delete',
    'pull_request',
    'pull_request_review',
    'check_suite',
    'status',
    'workflow_run',
    'deployment_status',
    'release',
];
export function gitWebhookEventsOf(kind) {
    return kind === 'gitlab' ? GITLAB_WEBHOOK_EVENTS : GITHUB_WEBHOOK_EVENTS;
}
/**
 * `APP_URL` có phải địa chỉ mà một máy **khác** gọi tới được không (§5.2).
 *
 * `localhost` / loopback ⇒ `false`: máy chủ git không bao giờ tới được, nên tab
 * nói "đang chạy bằng polling" thay vì mời bật một webhook chắc chắn hỏng. Địa
 * chỉ mạng nội bộ (`192.168.…`, tên máy trong công ty) **vẫn** là `true` —
 * GitLab tự host nằm cùng mạng gọi tới được.
 */
export function isPublicAppUrl(appUrl) {
    let host;
    try {
        host = new URL(appUrl).hostname.toLowerCase();
    }
    catch {
        return false;
    }
    // `URL` giữ ngoặc vuông của IPv6 (`[::1]`).
    const bare = host.replace(/^\[|\]$/g, '');
    if (bare === '' || bare === 'localhost' || bare.endsWith('.localhost'))
        return false;
    if (bare === '::1' || bare === '::' || bare === '0.0.0.0')
        return false;
    return !/^127(\.\d{1,3}){3}$/.test(bare);
}
/** `PUT …/webhook` — chỉ chế độ; bí mật luôn do server sinh. */
export const ChangeGitWebhookInput = z.object({ mode: GitWebhookMode });
/** `GET …/webhook` — đủ để vẽ tình trạng webhook của một kho và màn hướng dẫn dán tay. */
export const GitWebhookSettingsView = z.object({
    /** `none` = chỉ polling · `manual` = người quản kho dán tay · `auto` = ChillPM tự đăng ký. */
    mode: GitWebhookMode,
    hostKind: GitHostKind,
    /** Địa chỉ dán vào máy chủ git — dựng từ `APP_URL`. Bí mật **không** nằm trên URL. */
    url: z.string(),
    /** `false` ⇒ `APP_URL` là localhost: webhook không dùng được, tab chạy bằng polling. */
    publicUrl: z.boolean(),
    /** Lần gần nhất một lượt giao **đúng bí mật** tới nơi; `null` = chưa từng. */
    lastSeenAt: z.iso.datetime().nullable(),
    /** Sự kiện cần bật — khoá của `GITLAB_WEBHOOK_EVENTS` / `GITHUB_WEBHOOK_EVENTS`. */
    events: z.array(z.string()),
    /**
     * Token của kết nối đủ quyền tự đăng ký (vai Maintainer / quyền `admin` trên
     * kho lúc gắn) **và** `publicUrl`. Chỉ là gợi ý cho giao diện chọn nút mặc
     * định: bật `auto` vẫn có thể nhận `GIT_FORBIDDEN` (quyền đã đổi từ lúc gắn).
     */
    canAutoRegister: z.boolean(),
});
/** Phản hồi của `PUT …/webhook`. */
export const GitWebhookChangeResult = z.object({
    settings: GitWebhookSettingsView,
    /**
     * Bí mật vừa sinh — **chỉ** có khi vừa bật `manual`, và chỉ ở phản hồi này:
     * server không bao giờ trả lại nó. Mất thì bật `manual` lần nữa để sinh cái mới.
     */
    secret: z.string().nullable(),
    /**
     * Webhook cũ do ChillPM tự đăng ký đã được gỡ ở máy chủ git chưa. `null` =
     * không có gì để gỡ. `false` = không gỡ được (token hết quyền, máy chủ không
     * trả lời) ⇒ nhắc người quản kho gỡ tay — nó vô hại (mọi lượt giao nhận 401 /
     * 404) nhưng nằm lại trong danh sách webhook của kho.
     */
    remoteRemoved: z.boolean().nullable(),
});
//# sourceMappingURL=git-webhook.js.map